import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowDownAZ, ArrowUpAZ, ChevronLeft, RefreshCw, Search, X } from 'lucide-react';
import type { ProductStatus, RoiGrade, ScoredProduct } from '@sourcito/schemas';
import { Badge, Button, Card, Input } from '../design';
import { useProducts } from '../lib/products';
import { CategorySelect } from '../components/CategorySelect';

type SortField = 'roi' | 'bsr' | 'price' | 'recent';
type SortDir = 'asc' | 'desc';
type StatusFilter = 'active' | 'all' | ProductStatus;

const GRADE_OPTIONS: Array<{ value: RoiGrade | 'all'; label: string }> = [
  { value: 'all', label: 'All grades' },
  { value: 'A', label: 'A only' },
  { value: 'B', label: 'B+' },
  { value: 'C', label: 'C+' },
  { value: 'D', label: 'D+' },
];

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'active', label: 'Active' }, // hidePassed=true (default)
  { value: 'pending', label: 'Pending' },
  { value: 'interested', label: 'Interested' },
  { value: 'pursuing', label: 'Pursuing' },
  { value: 'passed', label: 'Passed' },
  { value: 'all', label: 'All' },
];

export function ProductsPage() {
  // Phase 1.8 Pass 5 — accept `searchRunId` + status presets via the URL so
  // /runs (View products →) + the review-queue header chip deeplink cleanly.
  const urlSearchParams =
    typeof window === 'undefined' ? null : new URLSearchParams(window.location.search);
  const initialSearchRunId = urlSearchParams?.get('searchRunId') ?? null;
  const initialUrlStatus = urlSearchParams?.get('status') ?? null;
  const initialUrlSort = urlSearchParams?.get('sortBy') ?? null;

  const [searchRunId, setSearchRunId] = useState<string | null>(initialSearchRunId);
  const [gradeFilter, setGradeFilter] = useState<RoiGrade | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => {
    if (initialUrlStatus && STATUS_OPTIONS.some((o) => o.value === initialUrlStatus)) {
      return initialUrlStatus as StatusFilter;
    }
    return 'active';
  });
  const [brandFilter, setBrandFilter] = useState('');
  const [brandQuery, setBrandQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [sort, setSort] = useState<{ field: SortField; dir: SortDir }>(() => ({
    field: initialUrlSort === 'recent' ? 'recent' : 'roi',
    dir: 'desc',
  }));

  const { data, isLoading, isError, error, refetch, isFetching } = useProducts({
    gradeMin: gradeFilter === 'all' ? undefined : gradeFilter,
    brand: brandFilter || undefined,
    category: categoryFilter ?? undefined,
    status: statusFilter === 'active' || statusFilter === 'all' ? undefined : statusFilter,
    hidePassed: statusFilter === 'active' ? true : statusFilter === 'all' ? false : undefined,
    searchRunId: searchRunId ?? undefined,
    sortBy: sort.field,
    sortOrder: sort.dir,
    limit: 100,
  });

  function setSortField(field: SortField) {
    setSort((s) =>
      s.field === field
        ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: field === 'bsr' ? 'asc' : 'desc' }
    );
  }

  function applyBrandFilter(e: React.FormEvent) {
    e.preventDefault();
    setBrandFilter(brandQuery.trim());
  }

  function clearBrand() {
    setBrandFilter('');
    setBrandQuery('');
  }

  const items = data?.items ?? [];

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <header className="border-b border-border-subtle">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Link
              to="/search"
              className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
            >
              <ChevronLeft className="size-5" />
            </Link>
            <div>
              <h1 className="text-xl font-medium leading-tight">Products</h1>
              <p className="text-xs text-fg-tertiary">
                Every ASIN ever surfaced by an NL search, scored against your fee profile.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              aria-label="Refresh"
              onClick={() => refetch()}
              loading={isFetching}
            >
              <RefreshCw className="size-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-10 space-y-4">
        {searchRunId && (
          <Card className="border-accent/30 bg-accent/[0.06]">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-fg-secondary">
                Filtered to products from one Keepa run.{' '}
                <span className="text-fg-tertiary font-mono text-xs">
                  ({searchRunId.slice(0, 8)})
                </span>
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchRunId(null);
                  if (typeof window !== 'undefined') {
                    const url = new URL(window.location.href);
                    url.searchParams.delete('searchRunId');
                    window.history.replaceState(null, '', url.toString());
                  }
                }}
                leadingIcon={<X className="size-3.5" />}
              >
                Clear run filter
              </Button>
            </div>
          </Card>
        )}
        <Card>
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <label className="text-xs uppercase tracking-wider text-fg-tertiary block mb-1.5">
                Status
              </label>
              <div className="flex items-center gap-1 rounded-md bg-surface-elev border border-border-subtle p-0.5">
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setStatusFilter(opt.value)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-xs transition-colors duration-fast ${
                      statusFilter === opt.value
                        ? 'bg-accent/[0.18] text-accent-fg'
                        : 'text-fg-secondary hover:text-fg-primary'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-fg-tertiary block mb-1.5">
                ROI grade
              </label>
              <div className="flex items-center gap-1 rounded-md bg-surface-elev border border-border-subtle p-0.5">
                {GRADE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setGradeFilter(opt.value)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-xs transition-colors duration-fast ${
                      gradeFilter === opt.value
                        ? 'bg-accent/[0.18] text-accent-fg'
                        : 'text-fg-secondary hover:text-fg-primary'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="min-w-[180px]">
              <label
                htmlFor="category-filter"
                className="text-xs uppercase tracking-wider text-fg-tertiary block mb-1.5"
              >
                Category
              </label>
              <CategorySelect
                id="category-filter"
                value={categoryFilter}
                onChange={setCategoryFilter}
              />
            </div>

            <form onSubmit={applyBrandFilter} className="flex-1 min-w-[200px] max-w-sm">
              <label
                htmlFor="brand-filter"
                className="text-xs uppercase tracking-wider text-fg-tertiary block mb-1.5"
              >
                Brand contains
              </label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-fg-tertiary pointer-events-none" />
                <Input
                  id="brand-filter"
                  type="text"
                  value={brandQuery}
                  onChange={(e) => setBrandQuery(e.target.value)}
                  placeholder="e.g. Apple"
                  className="pl-8 pr-8"
                />
                {brandFilter && (
                  <button
                    type="button"
                    onClick={clearBrand}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-tertiary hover:text-fg-primary"
                    aria-label="Clear brand filter"
                  >
                    <X className="size-4" />
                  </button>
                )}
              </div>
            </form>

            <div className="text-xs text-fg-tertiary tabular-nums">
              {data ? `${items.length} of ${items.length} loaded` : ''}
            </div>
          </div>
        </Card>

        {isLoading && <p className="text-sm text-fg-secondary">Loading…</p>}
        {isError && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm text-negative">
              Failed to load products: {(error as Error).message}
            </p>
          </Card>
        )}

        {data && items.length === 0 && (
          <Card className="text-center py-10">
            <p className="text-sm text-fg-secondary">
              No products match these filters yet.{' '}
              {gradeFilter === 'all' && !brandFilter ? (
                <>
                  <Link to="/search" className="text-accent-fg hover:underline">
                    Run a search
                  </Link>{' '}
                  to start populating the database.
                </>
              ) : (
                'Try clearing filters.'
              )}
            </p>
          </Card>
        )}

        {items.length > 0 && (
          <div className="border border-border-subtle rounded-md bg-surface overflow-hidden">
            <table className="w-full text-sm tabular-nums">
              <thead className="border-b border-border-subtle bg-surface-elev">
                <tr className="text-xs uppercase tracking-wider text-fg-tertiary">
                  <th className="text-left px-4 py-2.5 font-medium">ASIN</th>
                  <th className="text-left px-4 py-2.5 font-medium">Title</th>
                  <th className="text-left px-4 py-2.5 font-medium">Brand</th>
                  <SortHeader label="BSR" field="bsr" sort={sort} onClick={setSortField} />
                  <SortHeader
                    label="Price"
                    field="price"
                    sort={sort}
                    onClick={setSortField}
                    align="right"
                  />
                  <th className="text-right px-4 py-2.5 font-medium">Sales/mo</th>
                  <SortHeader
                    label="ROI"
                    field="roi"
                    sort={sort}
                    onClick={setSortField}
                    align="right"
                  />
                  <th className="text-center px-4 py-2.5 font-medium">Grade</th>
                  <th className="text-left px-4 py-2.5 font-medium">Status</th>
                  <SortHeader label="Refreshed" field="recent" sort={sort} onClick={setSortField} />
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <ProductRow key={p.id} p={p} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function ProductRow({ p }: { p: ScoredProduct }) {
  const refreshed = new Date(p.keepaRefreshedAt);
  return (
    <tr className="border-t border-border-subtle hover:bg-surface-elev">
      <td className="px-4 py-2.5 font-mono text-xs">
        <Link
          to="/products/$asin"
          params={{ asin: p.asin }}
          className="hover:underline text-accent-fg"
        >
          {p.asin}
        </Link>
      </td>
      <td className="px-4 py-2.5 max-w-md truncate">{p.title ?? '—'}</td>
      <td className="px-4 py-2.5">{p.brand ?? '—'}</td>
      <td className="px-4 py-2.5">{p.currentBsr === null ? '—' : p.currentBsr.toLocaleString()}</td>
      <td className="px-4 py-2.5 text-right">
        {p.currentPriceCents === null ? '—' : `$${(p.currentPriceCents / 100).toFixed(2)}`}
      </td>
      <td className="px-4 py-2.5 text-right">
        {p.monthlySalesEstimate === null ? '—' : p.monthlySalesEstimate.toLocaleString()}
      </td>
      <td className="px-4 py-2.5 text-right">
        {p.estimatedRoiPct === null ? '—' : `${(p.estimatedRoiPct * 100).toFixed(0)}%`}
      </td>
      <td className="px-4 py-2.5 text-center">
        {p.roiGrade ? <GradeBadge grade={p.roiGrade} /> : '—'}
      </td>
      <td className="px-4 py-2.5">
        <StatusBadge status={p.status} />
      </td>
      <td className="px-4 py-2.5 text-xs text-fg-tertiary">
        <time dateTime={p.keepaRefreshedAt} title={refreshed.toLocaleString()}>
          {relativeTime(refreshed)}
        </time>
      </td>
    </tr>
  );
}

function StatusBadge({ status }: { status: ProductStatus }) {
  const variant =
    status === 'pursuing'
      ? 'positive'
      : status === 'interested'
        ? 'accent'
        : status === 'passed'
          ? 'neutral'
          : 'neutral';
  return (
    <Badge variant={variant} className="text-[10px]">
      {status}
    </Badge>
  );
}

function SortHeader({
  label,
  field,
  sort,
  onClick,
  align,
}: {
  label: string;
  field: SortField;
  sort: { field: SortField; dir: SortDir };
  onClick: (f: SortField) => void;
  align?: 'left' | 'right';
}) {
  const active = sort.field === field;
  return (
    <th
      className={`px-4 py-2.5 font-medium cursor-pointer select-none ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => onClick(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active &&
          (sort.dir === 'asc' ? (
            <ArrowUpAZ className="size-3" />
          ) : (
            <ArrowDownAZ className="size-3" />
          ))}
      </span>
    </th>
  );
}

function GradeBadge({ grade }: { grade: RoiGrade }) {
  const variant =
    grade === 'A' || grade === 'B' ? 'positive' : grade === 'C' ? 'warning' : 'negative';
  return <Badge variant={variant}>{grade}</Badge>;
}

function relativeTime(date: Date): string {
  const diff = Date.now() - date.getTime();
  const min = 60_000;
  const hour = 60 * min;
  const day = 24 * hour;
  if (diff < min) return 'just now';
  if (diff < hour) return `${Math.round(diff / min)}m ago`;
  if (diff < day) return `${Math.round(diff / hour)}h ago`;
  return `${Math.round(diff / day)}d ago`;
}
